/**
 * File: useClientsQueries.ts
 * Path: apps/admin/src/shared/hooks/useClientsQueries.ts
 * Description: React Query wrappers over the clients repository.
 */

import { useQuery } from "@tanstack/react-query";
import {
  FIRESTORE_COLLECTIONS,
} from "@idk/config";
import { clientQuotesRepo } from "../services/clientQuotesService";
import { clientsRepo } from "../services/clientsService";

/** Shared with the leads hooks: winning a lead creates a client. */
export const clientsKey = [FIRESTORE_COLLECTIONS.clients] as const;
const listKey = [...clientsKey, "list"] as const;
const detailKey = (id: string) => [...clientsKey, "detail", id] as const;
/** Every client's project list; refreshed when a project's switches change. */
export const clientProjectsKey = [...clientsKey, "projects"] as const;
const projectsKey = (ids: string[]) => [...clientProjectsKey, ...ids] as const;
export const jobsKey = (id: string) => [...clientsKey, "jobs", id] as const;
/** Every client's jobs in one read (the Projects list). Refreshed with any one client's jobs. */
export const allJobsKey = [...clientsKey, "jobs", "all"] as const;
export const useAllClientJobs = () =>
  useQuery({ queryKey: allJobsKey, queryFn: clientsRepo.allJobs });
const activityKey = (id: string) => [...clientsKey, "activity", id] as const;
/** `enabled: false` for people the rules don't let read clients. */
export const useClientsList = (enabled = true) =>
  useQuery({ queryKey: listKey, queryFn: clientsRepo.list, enabled });

export const useClient = (id: string | undefined) =>
  useQuery({
    queryKey: detailKey(id ?? ""),
    queryFn: () => clientsRepo.get(id as string),
    enabled: Boolean(id),
  });

export const useClientProjects = (projectIds: string[]) =>
  useQuery({
    queryKey: projectsKey(projectIds),
    queryFn: () => clientsRepo.projects(projectIds),
    enabled: projectIds.length > 0,
  });

export const useClientJobs = (id: string | undefined) =>
  useQuery({
    queryKey: jobsKey(id ?? ""),
    queryFn: () => clientsRepo.jobs(id as string),
    enabled: Boolean(id),
  });
export const useClientActivity = (id: string | undefined) =>
  useQuery({
    queryKey: activityKey(id ?? ""),
    queryFn: () => clientsRepo.activity(id as string),
    enabled: Boolean(id),
  });

/** Read-only: the quotes feature owns the collection. */
export const useClientQuotes = (client: {
  id: string;
  leadId: string | null;
}) =>
  useQuery({
    queryKey: [...clientsKey, "quotes", client.id, client.leadId ?? ""],
    queryFn: () => clientQuotesRepo.forClient(client.id, client.leadId),
  });
